import { useState, useRef } from "react";

// Larger interactive area chart for price history. `data` = array of numbers.
export default function PriceChart({ data = [], up = true, height = 240 }) {
  const [hover, setHover] = useState(null);
  const wrapRef = useRef(null);
  const W = 640;
  const H = height;
  const padY = 16;

  if (!data || data.length < 2) return <div style={{ height }} className="grid place-items-center text-slate-600 text-sm">No chart data</div>;

  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const step = W / (data.length - 1);
  const y = (v) => padY + (H - padY * 2) - ((v - min) / range) * (H - padY * 2);
  const pts = data.map((v, i) => `${(i * step).toFixed(2)},${y(v).toFixed(2)}`).join(" ");
  const color = up ? "#34D399" : "#F87171";
  const area = `0,${H} ${pts} ${W},${H}`;

  const onMove = (e) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * W;
    let idx = Math.round(x / step);
    idx = Math.max(0, Math.min(data.length - 1, idx));
    setHover({ idx, px: idx * step, py: y(data[idx]), val: data[idx] });
  };

  const gridLines = [0, 0.25, 0.5, 0.75, 1].map((f) => padY + f * (H - padY * 2));

  return (
    <div ref={wrapRef} className="relative w-full" onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none">
        <defs>
          <linearGradient id="pc-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {gridLines.map((gy, i) => (
          <line key={i} x1="0" y1={gy} x2={W} y2={gy} stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
        ))}
        <polygon points={area} fill="url(#pc-grad)" />
        <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {hover && (
          <g>
            <line x1={hover.px} y1={padY} x2={hover.px} y2={H - padY} stroke="rgba(245,158,11,0.5)" strokeWidth="1" strokeDasharray="3 3" />
            <circle cx={hover.px} cy={hover.py} r="4" fill={color} stroke="#050507" strokeWidth="2" />
          </g>
        )}
      </svg>
      {hover && (
        <div className="absolute top-1 pointer-events-none glass rounded-lg px-2.5 py-1 text-xs font-mono-fin text-amber-300"
          style={{ left: `calc(${(hover.px / W) * 100}% + 6px)`, transform: (hover.px / W) > 0.8 ? "translateX(-110%)" : "none" }}>
          ${hover.val.toLocaleString("en-US", { maximumFractionDigits: hover.val < 10 ? 4 : 2 })}
        </div>
      )}
    </div>
  );
}
